import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import { Popover2Props as BlueprintPopover2Props } from '@blueprintjs/popover2';
import {
  ItemListRenderer as BlueprintItemListRenderer,
  ItemsEqualProp as BlueprintItemsEqualProp,
  Select2 as BlueprintSelect2,
  Select2Props as BlueprintSelect2Props,
} from '@blueprintjs/select';
import { ChevronDown, ChevronUp, Search } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { FixedSizeList } from 'react-window';

import { Input, InputProps } from 'bones/design-system/components/form/Input';
import {
  Icon,
  IconComponentType,
} from 'bones/design-system/components/icons/Icon';
import { SystemMessage } from 'bones/design-system/components/notifications/SystemMessage';
import { createRenderer } from 'bones/design-system/components/select/renderers/createRenderer';
import {
  ItemRendererOptions,
  isItemSelected,
  itemRenderer,
} from 'bones/design-system/components/select/renderers/itemRenderer';
import { listRenderer } from 'bones/design-system/components/select/renderers/listRenderer';
import { loadingRenderer } from 'bones/design-system/components/select/renderers/loadingRenderer';
import { virtualizedRenderer } from 'bones/design-system/components/select/renderers/virtualizedRenderer';
import {
  DIVIDER_VALUE,
  createDivider,
} from 'bones/design-system/utils/select/createDivider';
import { createNewItem } from 'bones/design-system/utils/select/createNewItem';
import { itemPredicate } from 'bones/design-system/utils/select/itemPredicate';
import { listPredicate } from 'bones/design-system/utils/select/listPredicate';
import {
  BaseSelectItem,
  SelectItem,
} from 'bones/design-system/utils/select/types';
import { isNotEqual } from 'bones/utils/isEqual';
import { noop } from 'bones/utils/noop';

export type SelectorProps<
  SubItemType extends SelectItem | null,
  ItemType extends SelectItem<SubItemType>,
  SelectionType extends
    | SubItemType
    | ItemType
    | SubItemType[]
    | ItemType[]
    | null
> = ItemRendererOptions<SubItemType, ItemType, SelectionType> & {
  // required
  inputProps: Omit<
    InputProps,
    | 'autoFocus'
    | 'disabled'
    | 'onChange'
    | 'onClear'
    | 'onClick'
    | 'onSelectorClick'
    | 'readOnly'
    | 'selectorIcon'
  >;
  items: ItemType[];
  onSelect: (value: SelectionType, event?: React.MouseEvent) => any;

  // optional
  className?: string;
  creatable?: boolean;
  createNewItemFromQuery?: BlueprintSelect2Props<ItemType>['createNewItemFromQuery'];
  createNewItemTestId?: string;
  createText?: (query: string) => string;
  disabled?: boolean;
  filter?: <T extends SelectItem<SubItemType>>(
    item: T,
    searchText: string
  ) => boolean;
  filterable?: boolean;
  filterIcon?: IconComponentType;
  filterPlaceholder?: string;
  filterTestId?: string;
  itemsEqual?: BlueprintItemsEqualProp<ItemType>;
  keepOpenOnSelect?: boolean;
  loading?: boolean;
  loadingTestId?: string;
  noResults?: string;
  onQueryChange?: (query: string) => any;
  popoverProps?: BlueprintPopover2Props;
  resetOnClose?: BlueprintSelect2Props<ItemType>['resetOnClose'];
  sortSelectedFirst?: boolean;
  virtualized?: boolean;
};

type SelectorState<
  SubItemType extends SelectItem | null,
  ItemType extends SelectItem<SubItemType>
> = {
  isOpen: boolean;
  prevItems: ItemType[];
  sortedItems: ItemType[];
};

export type SingleSelector<
  SubItemType extends SelectItem | null = null,
  ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>
> = Selector<SubItemType, ItemType, ItemType | null>;

export type MultiSelector<
  SubItemType extends SelectItem | null = null,
  ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>
> = Selector<SubItemType, ItemType, ItemType[]>;

export class Selector<
  SubItemType extends SelectItem | null = null,
  ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>,
  SelectionType extends
    | SubItemType
    | ItemType
    | null
    | SubItemType[]
    | ItemType[] = ItemType[]
> extends React.Component<
  SelectorProps<SubItemType, ItemType, SelectionType>,
  SelectorState<SubItemType, ItemType>
> {
  public static Single<
    T extends BaseSelectItem | null = null,
    U extends SelectItem<T> = SelectItem<T>,
    S extends T | U | null = T | U | null
  >({
    selectorRef,
    ...props
  }: SelectorProps<T, U, S> & {
    selectorRef?: React.Ref<Selector<T, U, S>>;
  }) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Selector<T, U, S> ref={selectorRef} {...props} />;
  }

  public static Multi = <
    T extends BaseSelectItem | null = null,
    U extends SelectItem<T> = SelectItem<T>,
    S extends T[] | U[] = T[] | U[]
  >({
    selectorRef,
    ...props
  }: SelectorProps<T, U, S> & {
    selectorRef?: React.Ref<Selector<T, U, S>>;
  }) => {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Selector<T, U, S> ref={selectorRef} {...props} checkbox />;
  };

  static getSortedItems<
    SubItemType extends SelectItem | null = null,
    ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>,
    SelectionType extends
      | SubItemType
      | ItemType
      | null
      | SubItemType[]
      | ItemType[] = ItemType[]
  >(props: Readonly<SelectorProps<SubItemType, ItemType, SelectionType>>) {
    const { items, selected, sortSelectedFirst } = props;

    if (!sortSelectedFirst) {
      return items;
    }

    const itemsSelected: ItemType[] = [];
    const itemsNotSelected: ItemType[] = [];
    items.forEach((item) => {
      if (isItemSelected(item, selected)) {
        itemsSelected.push(item);
      } else {
        itemsNotSelected.push(item);
      }
    });
    if (itemsSelected.length) {
      const itemsNotSelectedWithoutUselessDividers = itemsNotSelected.filter(
        (item, i) =>
          item.value !== DIVIDER_VALUE ||
          (itemsNotSelected[i + 1] &&
            itemsNotSelected[i + 1].value !== DIVIDER_VALUE)
      );
      return [
        ...itemsSelected,
        createDivider() as ItemType,
        ...itemsNotSelectedWithoutUselessDividers,
      ];
    }
    return items;
  }

  static getDerivedStateFromProps<
    SubItemType extends SelectItem | null = null,
    ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>,
    SelectionType extends
      | SubItemType
      | ItemType
      | null
      | SubItemType[]
      | ItemType[] = ItemType[]
  >(
    props: Readonly<SelectorProps<SubItemType, ItemType, SelectionType>>,
    state: SelectorState<SubItemType, ItemType>
  ): Partial<SelectorState<SubItemType, ItemType>> | null {
    const { items } = props;
    const { prevItems } = state;

    if (isNotEqual(items, prevItems)) {
      return {
        sortedItems: Selector.getSortedItems<
          SubItemType,
          ItemType,
          SelectionType
        >(props),
        prevItems: items,
      };
    }
    return null;
  }

  listRef: React.MutableRefObject<FixedSizeList | null> = React.createRef();

  constructor(props: SelectorProps<SubItemType, ItemType, SelectionType>) {
    super(props);

    this.state = {
      isOpen: false,
      prevItems: [],
      sortedItems: [],
    };
  }

  handleInputClick = () => {
    this.setState({
      isOpen: true,
      sortedItems: Selector.getSortedItems<
        SubItemType,
        ItemType,
        SelectionType
      >(this.props),
    });
  };

  handleSelectorClick = () => {
    this.setState((prevState) => ({
      isOpen: !prevState.isOpen,
      sortedItems: Selector.getSortedItems<
        SubItemType,
        ItemType,
        SelectionType
      >(this.props),
    }));
  };

  handleSelect = (
    maybeItem: ItemType | NonNullable<SubItemType>,
    evt?: React.SyntheticEvent<HTMLElement>
  ) => {
    if (maybeItem.value === DIVIDER_VALUE) {
      return;
    }

    let item: ItemType | NonNullable<SubItemType> = maybeItem;
    // eslint-disable-next-line prefer-destructuring
    const items: SubItemType[] | undefined = item.items;
    if (items) {
      const firstItem =
        item.collapseSingleItem && items.length === 1 && items[0];
      if (firstItem) {
        // select the first and only sub-item instead of selecting the sub-item parent
        item = firstItem;
      } else {
        // otherwise, do not allow selecting an item that contains sub-items
        return;
      }
    }

    const event = evt as React.MouseEvent<HTMLElement> | undefined;
    const { keepOpenOnSelect, selected, onSelect } = this.props;
    if (Array.isArray(selected)) {
      const newSelected = [...selected];
      if (event?.shiftKey) {
        const foundIndex = newSelected.findIndex(
          (selectedItem) => selectedItem.value === item.value
        );
        if (foundIndex > -1) {
          newSelected.splice(foundIndex, 1);
        } else {
          newSelected.push(item);
        }
      } else {
        newSelected.length = 0;
        newSelected.push(item);
        if (!keepOpenOnSelect) {
          this.setState({ isOpen: false });
        }
      }
      onSelect(newSelected as SelectionType, event);
    } else {
      if (!keepOpenOnSelect) {
        this.setState({ isOpen: false });
      }
      onSelect(item as SelectionType, event);
    }
  };

  handleClear = () => {
    const { selected, onSelect } = this.props;
    const selection = (Array.isArray(selected) ? [] : null) as SelectionType;
    onSelect(selection);
  };

  render() {
    const {
      checkbox,
      className,
      creatable,
      createNewItemFromQuery = createNewItem,
      createNewItemTestId,
      createText,
      disabled,
      filter,
      filterable,
      filterIcon = Search,
      filterPlaceholder,
      filterTestId,
      inputProps,
      itemClassName,
      items,
      itemsEqual,
      itemTestId,
      loading,
      loadingTestId,
      onQueryChange,
      popoverProps,
      resetOnClose,
      selected,
      subItemTestId,
      virtualized,
    } = this.props;

    const { isOpen, sortedItems } = this.state;

    const noResults = (
      <div className="vb-selector-no-results">
        <SystemMessage text="No matches" variant="text" />
      </div>
    );

    let itemListRenderer: BlueprintItemListRenderer<ItemType>;
    if (loading) {
      itemListRenderer = loadingRenderer<SubItemType, ItemType>({
        loadingTestId,
      });
    } else if (virtualized) {
      itemListRenderer = virtualizedRenderer<SubItemType, ItemType>({
        listRef: this.listRef,
        noResults,
      });
    } else {
      itemListRenderer = listRenderer<SubItemType, ItemType>({ noResults });
    }

    return (
      <BlueprintSelect2<ItemType>
        className={classNames('vb-selector', className)}
        createNewItemFromQuery={
          creatable
            ? (query) => {
                const trimmedQuery = query.trim();
                // Note: createNewItemFromQuery needs to be defined by user if not using SelectItem!
                const fn = createNewItemFromQuery as NonNullable<
                  BlueprintSelect2Props<ItemType>['createNewItemFromQuery']
                >;
                return fn(trimmedQuery);
              }
            : undefined
        }
        createNewItemRenderer={
          creatable
            ? createRenderer<SubItemType, ItemType>({
                createText,
              })(items, createNewItemTestId)
            : undefined
        }
        disabled={disabled}
        filterable={filterable}
        inputProps={{
          // @ts-ignore
          'data-testid': filterTestId,
          className: classNames(
            'vb-selector-filter-input-group',
            'vb-input-group'
          ),
          inputClassName: classNames(
            'vb-selector-filter-input-input',
            'vb-input-input'
          ),
          leftIcon: null,
          leftElement: (
            <Icon
              className="vb-input-icon"
              IconComponent={filterIcon}
              size="sm"
            />
          ),
          placeholder: filterPlaceholder,
        }}
        itemListPredicate={listPredicate<SubItemType, ItemType>({
          itemPredicate: itemPredicate(filter),
        })}
        itemListRenderer={itemListRenderer}
        itemRenderer={itemRenderer<SubItemType, ItemType, SelectionType>({
          checkbox,
          itemClassName,
          itemTestId,
          subItemTestId,
          selected,
          handleSelect: this.handleSelect,
        })}
        items={sortedItems}
        itemsEqual={itemsEqual}
        noResults={noResults}
        onItemSelect={this.handleSelect}
        onQueryChange={(query) => {
          onQueryChange?.(query);
          this.setState({
            sortedItems: Selector.getSortedItems<
              SubItemType,
              ItemType,
              SelectionType
            >(this.props),
          });
          window.requestAnimationFrame(() => {
            this.listRef.current?.scrollToItem(0, 'start');
          });
        }}
        popoverProps={{
          rootBoundary: 'document',
          position: BlueprintPopoverPosition.BOTTOM_LEFT,
          minimal: true,
          portalContainer:
            (process.env.NODE_ENV === 'test' &&
              document.getElementById('vet-bones-root')) ||
            // istanbul ignore next
            undefined,
          hasBackdrop: true,
          ...popoverProps,
          backdropProps: {
            ...popoverProps?.backdropProps,
            onClick: (event: React.MouseEvent<HTMLDivElement>) => {
              popoverProps?.backdropProps?.onClick?.(event);
              this.setState({ isOpen: false });
            },
          },
          isOpen,
          popoverClassName: classNames(
            'vb-selector-popover',
            `vb-popover-position--${
              popoverProps?.position ?? BlueprintPopoverPosition.BOTTOM_LEFT
            }`,
            popoverProps?.popoverClassName
          ),
        }}
        resetOnClose={resetOnClose}
        scrollToActiveItem={false}
      >
        <Input
          autoFocus={false}
          disabled={disabled}
          onChange={noop}
          onClear={this.handleClear}
          onClick={this.handleInputClick}
          onSelectorClick={this.handleSelectorClick}
          readOnly
          selectorIcon={isOpen ? ChevronUp : ChevronDown}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...inputProps}
          className={classNames('vb-selector-input', inputProps.className)}
          clearable={inputProps.clearable ?? false}
        />
      </BlueprintSelect2>
    );
  }
}
